import Head from 'next/head';
import Markdown from 'react-markdown';
import rehypeSlug from 'rehype-slug';
import styled from 'styled-components';

import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { FeatureFlags } from 'featureFlags/constants';
import { pagesContentMap } from 'termsAndPolicy/utils/pagesContentMap';
import { pageNavLinks } from 'termsAndPolicy/utils/pagesNavLinks';
import { TermsAndPolicyNavigation } from 'termsAndPolicy/components/TermsAndPolicyNavigation';
import { variableFont } from 'styles/theme/text';

import { IPage } from 'shared/types/Page';
import { usePageMetadata } from 'shared/hooks/usePageMetadata';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const Pages = ({ pageContent, navLinks }: { pageContent: IPage; navLinks: IPage[] }) => {
  const { pageTitle } = usePageMetadata({
    defaultTitle: `${pageContent?.title}  `,
  });

  return (
    <Pages.Container>
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={pageTitle} key="og:title" />
      </Head>
      <TermsAndPolicyNavigation pages={navLinks} />
      <Pages.Content>
        <Markdown rehypePlugins={[rehypeSlug]}>{pageContent?.content}</Markdown>
      </Pages.Content>
    </Pages.Container>
  );
};

export const getServerSideProps = async ({ req, res, query, locale }) => {
  const { pageSlug, app }: { app: string; pageSlug: string } = query;
  const { featureFlags } = req;

  try {
    const pages: IPage[] = await sneakersRequest(req, res).get('api/v1/pages').json();
    const pageMap = pagesContentMap(pages);
    const navLinks: IPage[] = pageNavLinks(pages);

    // the mobile apps want this page to go to how to play if they append this query param
    if (
      pageSlug?.toLowerCase() === 'blackfridayfaq' &&
      featureFlags?.[FeatureFlags.WEB_ENABLE_HOW_TO_PLAY_MOBILE_REDIRECT] &&
      app === 'y'
    ) {
      return {
        redirect: {
          permanent: true,
          destination: getRedirectionPath('/howtoplay', locale),
        },
      };
    }

    // Check if page for this slug exists, if so return page content
    if (pageMap[pageSlug]) {
      return {
        props: {
          ...(await setServerSideTranslations({ locale })),
          pageContent: pageMap[pageSlug],
          navLinks,
        },
      };
    }

    if (pageSlug?.toLowerCase() === 'psg') {
      return {
        redirect: {
          permanent: true,
          destination: getRedirectionPath('/brand/paris-saint-germain', locale),
        },
      };
    }
    return { notFound: true };
  } catch {
    return { notFound: true };
  }
};

Pages.Container = styled.div`
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  color: ${({ theme }) => theme.navigation.textColor};
  display: flex;
  justify-content: flex-start;
  padding: 32px 16px;
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 32px;
    justify-content: center;
  }
`;

Pages.Content = styled.div`
  display: flex;
  flex-direction: column;

  h1 {
    margin-bottom: 32px;
    text-transform: uppercase;
    ${variableFont({ size: '11.5px', height: '16px', fontWeight: '600' })};
  }

  h2,
  h3 {
    margin-bottom: 8px;
    margin-top: 40px;
    ${variableFont({ size: '11.5px', height: '16px', fontWeight: '600' })};
  }

  a {
    text-decoration: underline;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-width: 418px;
  }
`;

export default Pages;
